import {map, of} from 'rxjs';
import Decimal from './break_infinity';
import {CompositeShape, ShapeLayer} from './CompositeShape';
import {Landmark} from './landmark';
import {createLine, Region} from './Region';
import {ri} from './runner-idle';
import {Vector} from './Vector.js';
import * as D from './decimal_utils';
import {any$} from './utils';
import {
  StatusEffect,
  makeBlindTemplate,
  makeBurnTemplate,
  makePoisonTemplate,
  makeUnBlindTemplate,
} from './status-effect';
import {
  makeApplyStatusEffectToPlayerAction,
  makeTakeDamageAction,
  trackPlayerAction,
} from './actions';
import {mimicTemplate, slimeTemplate} from './creature_templates';
import {Creature} from './creature';
import {Actor} from './actor';

export const landmarkNames = [
  // Temporary items
  'resources',
  'guidebook',
  'fishingPole',
  // Traveling stuff
  'road',
  // Water sources
  'stream',
  'lake',
  // Main storyline
  'gym',
  'track',
  'crossroads',
  'library',
  'plains',
  'monastery',
  'forest',
  'darkForest',
  'mountain',
  'mine',
  'village',
  // Demo stuff
  'sign1',
  'demoSign',
  'chest1',
  'chest2',
  'chest3',
  'chest4',
  'trap1',
  'trap2',
  'trap3',
  'trap4',
  'trap5',
  'trap6',
  'trap7',
  'trap8',
  'trap9',
  'trap10',
  'trap11',
  'trap12',
  'trap13',
  'trap14',
  'trap15',
  'trap16',
  'trap17',
  'trap17b',
] as const;

export function initializeLandmarks() {
  function createLandmark(overrides: Partial<Landmark> = {}) {
    if (!overrides.name) {
      throw new Error('Landmark must have a name');
    }
    // ri.landmarks[overrides.name] = document.createElement('ri-landmark');
    ri.landmarks[overrides.name].setup(overrides);
  }

  // createLandmark({
  //   name: 'nowhere',
  //   displayName: 'Nowhere',
  //   description: `Just running in place. Very useful..`,
  //   distance: Decimal.POSITIVE_INFINITY,
  //   _visibleCondition: T,
  //   hideEntry: true,
  // });

  // createLandmark({
  //   name: 'arena',
  //   displayName: 'Arena',
  //   description:
  //     'You have reached the Arena! You see a big sign: "UNDER CONSTRUCTION".',
  //   distance: new Decimal(1e3),
  //   unlocks: [],
  // });

  createLandmark({
    name: 'resources',
    displayName: 'Resources Journal',
    description: `A journal automagically keeping track of your Resources, which include some esoteric things!`,
    shape: new Vector(0, 5),
    temporary: true,
  });

  createLandmark({
    name: 'demoSign',
    displayName: 'Demo Area - For Testing/Dev',
    description: `Everything in here is just for demoing / testing mechanics. Unstable, unbalanced, and subject to change.`,
    shape: new Region({
      topLeft: new Vector(-10, -2),
      width: new Decimal(200),
      height: new Decimal(30),
      fillColor: [200, 200, 200],
      strokeColor: [0, 0, 0],
    }),
    speed: new Decimal(2),
    tags: new Set(['multiplySpeed']),
    navigatable$: of(false),
  });

  createLandmark({
    name: 'sign1',
    displayName: '',
    description: ``,
    shape: new Region({
      name: 'Follow the road for a semi-guided journey,\nor explore at your own risk!',
      topLeft: new Vector(-4, -1),
      width: new Decimal(9),
      height: new Decimal(1),
      fillColor: [200, 200, 200, 0],
      strokeColor: [0, 0, 0, 0],
    }),
    navigatable$: of(false),
  });

  createLandmark({
    name: 'chest1',
    displayName: 'Wooden Chest',
    description: `A box of meat?`,
    shape: new Vector(-2, -5),
    loot: {meat: 5},
    temporary: true,
    navigatable$: of(false),
  });

  createLandmark({
    name: 'chest2',
    displayName: 'Glasses',
    description: `Boosts your Perception`,
    shape: new Vector(-4, -5),
    loot: {perception: 1},
    temporary: true,
    navigatable$: of(false),
  });

  createLandmark({
    name: 'chest3',
    displayName: 'Shoes',
    description: `Boosts your Speed`,
    shape: new Vector(-6, -5),
    loot: {speed: 1},
    temporary: true,
    navigatable$: of(false),
  });

  createLandmark({
    name: 'chest4',
    displayName: 'Abacus',
    description: `Boosts your Intelligence`,
    shape: new Vector(-8, -5),
    loot: {intelligence: 1},
    temporary: true,
    navigatable$: of(false),
  });

  createLandmark({
    name: 'trap1',
    navigatable$: of(false),
    displayName: 'Point Trap - Damage on Move Onto',
    description: 'Uh-oh.',
    shape: new Vector(2, -5),
    // temporary: true,
    loot: {health: -1},
    visibleOnMap$: any$([
      ri.landmarks.trap1.visited$,
      ri.stats.perception.value$.pipe(map(D.gte(2))),
    ]),
  });

  createLandmark({
    name: 'trap2',
    navigatable$: of(false),
    displayName: 'Area Trap - Damage on Move Onto/In',
    description: 'Uh-oh!',
    shape: new Region({
      topLeft: new Vector(4, -5),
      width: new Decimal(2),
      height: new Decimal(2),
    }),
    // temporary: true,
    loot: {health: -1},
    visibleOnMap$: any$([
      ri.landmarks.trap2.visited$,
      ri.stats.perception.value$.pipe(map(D.gte(2))),
    ]),
  });

  createLandmark({
    name: 'trap3',
    navigatable$: of(false),
    displayName: 'One-time Trap - Damage on Move Onto, Destroys Self',
    description: 'Uh.',
    shape: new Vector(8, -5),
    temporary: true,
    loot: {health: -1},
    visibleOnMap$: any$([
      ri.landmarks.trap3.visited$,
      ri.stats.perception.value$.pipe(map(D.gte(2))),
    ]),
  });

  createLandmark({
    name: 'trap4',
    navigatable$: of(false),
    displayName: 'Poison Trap - Applies Poison on Move Onto',
    description: 'Uh-oh. Uh-oh. Uh-oh.',
    shape: new Vector(10, -5),
    interactions: [
      () => ri.addStatusEffect(StatusEffect.create(makePoisonTemplate())),
    ],
    visibleOnMap$: any$([
      ri.landmarks.trap4.visited$,
      ri.stats.perception.value$.pipe(map(D.gte(2))),
    ]),
  });

  createLandmark({
    name: 'trap5',
    navigatable$: of(false),
    displayName: 'Flashbang Trap - Applies Blinded on Move Onto',
    description: "Hey, where'd everybody go?",
    shape: new Vector(12, -5),
    interactions: [
      () => {
        const blindStatusEffect = StatusEffect.create(makeBlindTemplate());
        ri.addStatusEffect(blindStatusEffect);
      },
    ],
    visibleOnMap$: any$([
      ri.landmarks.trap5.visited$,
      ri.stats.perception.value$.pipe(map(D.gte(2))),
    ]),
  });

  createLandmark({
    name: 'trap6',
    navigatable$: of(false),
    displayName: 'Summon Trap - Spawns Slimes',
    description: 'Calling reinforcements!',
    shape: new Vector(14, -5),
    interactions: [
      () => {
        const positions = [
          new Vector(18, 5),
          new Vector(10, 5),
          // new Vector(14, 1),
          // new Vector(14, 9),
        ];
        positions.forEach((position) => {
          const creature = Creature.create(slimeTemplate);
          creature.moveTo(position);
          ri.addActor(creature);
        });
      },
    ],
    visibleOnMap$: any$([
      ri.landmarks.trap6.visited$,
      ri.stats.perception.value$.pipe(map(D.gte(2))),
    ]),
  });

  createLandmark({
    name: 'trap7',
    navigatable$: of(false),
    displayName: 'Well-hidden Trap - Damage on Move Onto',
    description: 'Uh-oh.',
    shape: new Vector(16, -5),
    loot: {health: -1},
    visibleOnMap$: any$([
      ri.landmarks.trap7.visited$,
      ri.stats.perception.value$.pipe(map(D.gte(5))),
    ]),
  });

  createLandmark({
    name: 'trap8',
    navigatable$: of(false),
    displayName: 'Area Trap - Damage every tick (second here)',
    description: 'Uh-oh.',
    shape: new Region({
      topLeft: new Vector(18, -5),
      width: new Decimal(2),
      height: new Decimal(2),
    }),
    interactTick() {
      ri.takeDamage(new Decimal(1), ri.landmarks.trap8);
    },
    visibleOnMap$: any$([
      ri.landmarks.trap8.visited$,
      ri.stats.perception.value$.pipe(map(D.gte(2))),
    ]),
  });

  createLandmark({
    name: 'trap9',
    navigatable$: of(false),
    displayName: 'Blood Thorns - Damage on move onto & every tick',
    description: 'Uh-oh.',
    shape: new Region({
      topLeft: new Vector(22, -5),
      width: new Decimal(2),
      height: new Decimal(2),
    }),
    loot: {health: -1},
    interactTick() {
      ri.takeDamage(new Decimal(1), ri.landmarks.trap8);
    },
    visibleOnMap$: any$([
      ri.landmarks.trap9.visited$,
      ri.stats.perception.value$.pipe(map(D.gte(2))),
    ]),
  });

  createLandmark({
    name: 'trap10',
    navigatable$: of(false),
    displayName: 'Chest (TRAPPED)',
    description: "The chest was trapped! You've been poisoned!",
    shape: new Vector(26, -5),
    interactions: [
      () =>
        ri.addStatusEffect(
          StatusEffect.create(makePoisonTemplate({timeLeft: new Decimal(10)}))
        ),
    ],
    visibleOnMap$: any$([
      ri.landmarks.trap10.visited$,
      ri.stats.perception.value$.pipe(map(D.gte(2))),
    ]),
  });

  createLandmark({
    name: 'trap11',
    navigatable$: of(false),
    displayName: 'Chest (MIMIC)',
    description: 'The chest was a mimic!',
    shape: new Vector(28, -5),
    interactions: [
      () => {
        const creature = Creature.create(mimicTemplate);
        creature.moveTo(new Vector(28, -5));
        ri.addActor(creature);
      },
    ],
    temporary: true,
    visibleOnMap$: any$([
      ri.landmarks.trap11.visited$,
      ri.stats.perception.value$.pipe(map(D.gte(2))),
    ]),
  });

  createLandmark({
    name: 'trap12',
    navigatable$: of(false),
    displayName: 'Tar Trap',
    description: 'Makes tar',
    shape: new Vector(32, -5),
    interactions: [
      function () {
        this.shape = new Region({
          topLeft: new Vector(29, -2),
          width: new Decimal(6),
          height: new Decimal(6),
        });
        this.interactions = [];
      },
    ],
    speed: new Decimal(0.25),
    tags: new Set(['multiplySpeed']),
    visibleOnMap$: any$([
      ri.landmarks.trap12.visited$,
      ri.stats.perception.value$.pipe(map(D.gte(2))),
    ]),
  });

  createLandmark({
    name: 'trap13',
    navigatable$: of(false),
    displayName: 'Poison Tar Trap',
    description: 'Makes poisonous tar',
    shape: new Vector(39, -5),
    interactions: [
      function (this: Landmark) {
        this.shape = new Region({
          topLeft: new Vector(36, -2),
          width: new Decimal(6),
          height: new Decimal(6),
        });
        this.interactions = [
          () => {
            ri.addStatusEffect(
              StatusEffect.create(
                makePoisonTemplate({
                  timeLeft: new Decimal(10),
                })
              )
            );
          },
        ];
      },
    ],
    speed: new Decimal(0.25),
    tags: new Set(['multiplySpeed']),
    visibleOnMap$: any$([
      ri.landmarks.trap13.visited$,
      ri.stats.perception.value$.pipe(map(D.gte(2))),
    ]),
  });

  createLandmark({
    name: 'trap14',
    navigatable$: of(false),
    displayName: 'Burning Oil Trap',
    description: 'Makes burning oil',
    shape: new Vector(46, -5),
    interactions: [
      function (this: Landmark) {
        this.shape = new Region({
          topLeft: new Vector(43, -2),
          width: new Decimal(6),
          height: new Decimal(6),
        });
        this.interactions = [
          () => {
            ri.addStatusEffect(
              StatusEffect.create(
                makeBurnTemplate({
                  timeLeft: new Decimal(10),
                })
              )
            );
          },
        ];
      },
    ],
    speed: new Decimal(0.75),
    tags: new Set(['multiplySpeed']),
    visibleOnMap$: any$([
      ri.landmarks.trap14.visited$,
      ri.stats.perception.value$.pipe(map(D.gte(2))),
    ]),
  });
  createLandmark({
    name: 'trap15',
    navigatable$: of(false),
    displayName: 'Teleport Trap',
    description: 'Teleports player',
    shape: new Vector(51, -5),
    interactions: [
      function (this: Landmark) {
        const newPos = ri.position$.value.plus(Vector.random(10));
        ri.position$.next(newPos);
      },
    ],
    visibleOnMap$: any$([
      ri.landmarks.trap15.visited$,
      ri.stats.perception.value$.pipe(map(D.gte(2))),
    ]),
  });

  createLandmark({
    name: 'trap16',
    navigatable$: of(false),
    displayName: 'Lava',
    description: 'Burns player',
    shape: new Region({
      topLeft: new Vector(57, 7),
      width: new Decimal(4),
      height: new Decimal(20),
      fillColor: [240, 50, 0],
    }),
    interactTick(this: Landmark) {
      ri.addStatusEffect(StatusEffect.create(makeBurnTemplate({})));
    },
    speed: new Decimal(0.5),
    tags: new Set(['multiplySpeed']),
  });

  createLandmark({
    name: 'trap17',
    navigatable$: of(false),
    displayName: 'Trapped Bridge',
    description: 'Burns player',
    shape: new Region({
      topLeft: new Vector(55, -3),
      width: new Decimal(8),
      height: new Decimal(4),
    }),
    speed: new Decimal(2),
    tags: new Set(['blockInteractTick']),
    z: new Decimal(1),
  });

  createLandmark({
    name: 'trap17b',
    navigatable$: of(false),
    displayName: 'Pressure Plate',
    description: 'The bridge falls away beneath you!',
    shape: new Region({
      topLeft: new Vector(58.5, -3),
      width: new Decimal(1),
      height: new Decimal(4),
      fillColor: [255, 240, 240],
    }),
    interactions: [
      function (this: Landmark) {
        ri.landmarks.trap17.shape = new CompositeShape({
          shapes: [
            new ShapeLayer(
              new Region({
                topLeft: new Vector(55, -3),
                width: new Decimal(2.25),
                height: new Decimal(4),
              }),
              {}
            ),
            new ShapeLayer(
              new Region({
                topLeft: new Vector(60.75, -3),
                width: new Decimal(2.25),
                height: new Decimal(4),
              }),
              {}
            ),
          ],
        });
      },
    ],
    temporary: true,
    z: new Decimal(2),
  });

  createLandmark({
    name: 'village',
    displayName: 'Village',
    description: 'The Village! You see a big sign: "UNDER CONSTRUCTION"',
    shape: new Vector(-3000, 2500),
    navigatable$: ri.landmarks.library.visited$,
  });

  createLandmark({
    name: 'guidebook',
    displayName: 'Guidebook',
    description: `A book on the ground: "Unlockables: Your Guide to Going the Distance". Maybe it will help?`,
    shape: new Vector(0, 100),
    navigatable$: ri.landmarks.resources.visited$,
    temporary: true,
  });

  createLandmark({
    name: 'fishingPole',
    displayName: 'Fishing Pole',
    description: `A stick with some twine and a little metal hook.`,
    shape: new Vector(-5, 150),
    navigatable$: of(false),
    temporary: true,
  });

  createLandmark({
    name: 'gym',
    displayName: 'Gym',
    description:
      'A gym. Exercise your body! Unlocks various Training activities.',
    shape: new CompositeShape({})
      .addShape(
        new Region({
          name: 'Gym',
          topLeft: new Vector(-13, 265),
          width: new Decimal(8),
          height: new Decimal(15),
          fillColor: [45, 20, 5],
        })
      )
      .addShape(
        new Region({
          name: '',
          topLeft: new Vector(-12.5, 264.5),
          width: new Decimal(7),
          height: new Decimal(14),
          fillColor: [250, 225, 175],
        })
      ),
    navigatable$: ri.landmarks.guidebook.visited$,
    tags: new Set(['gym']),
  });

  const trackWidth = 3;
  createLandmark({
    name: 'track',
    displayName: 'Track',
    description: 'A running track.',
    speed: new Decimal(3),
    shape: new CompositeShape({})
      .addShape(
        createLine(new Vector(5, 250), new Vector(105, 250), {
          thickness: new Decimal(trackWidth),
          fillColor: [250, 225, 175],
        })
      )
      .addShape(
        createLine(new Vector(105, 250), new Vector(105, 300), {
          thickness: new Decimal(trackWidth),
          fillColor: [250, 225, 175],
        })
      )
      .addShape(
        createLine(new Vector(105, 300), new Vector(5, 300), {
          thickness: new Decimal(trackWidth),
          fillColor: [250, 225, 175],
        })
      )
      .addShape(
        createLine(new Vector(5, 300), new Vector(5, 250), {
          thickness: new Decimal(trackWidth),
          fillColor: [250, 225, 175],
        })
      ),
    navigatable$: ri.landmarks.gym.visited$,
    tags: new Set(['gym', 'passable', 'multiplySpeed']),
  });

  createLandmark({
    name: 'crossroads',
    displayName: 'Crossroads',
    description: 'A great crossing of roads.',
    shape: new Vector(0, 1000),
    navigatable$: ri.landmarks.gym.visited$,
  });

  const roadWidth = 4; // 4m / 12ft is a pretty standard road width
  createLandmark({
    name: 'road',
    displayName: 'Road',
    description:
      "The Royal Road! Or maybe just somebody's road, you're not quite sure.",
    speed: new Decimal(2),
    tags: new Set(['passable', 'multiplySpeed']),
    shape: new CompositeShape({})
      .addShape(
        createLine(new Vector(0, 0), new Vector(0, 1000), {
          thickness: new Decimal(roadWidth),
          fillColor: [196, 196, 196],
        })
      )
      .addShape(
        createLine(new Vector(-1000, 2000), new Vector(0, 1000), {
          thickness: new Decimal(4),
          fillColor: [196, 196, 196],
        })
      )
      .addShape(
        createLine(new Vector(1000, 3000), new Vector(0, 1000), {
          thickness: new Decimal(4),
          fillColor: [196, 196, 196],
        })
      )
      .addShape(
        createLine(new Vector(-1000, 1000), new Vector(500, 1000), {
          thickness: new Decimal(4),
          fillColor: [196, 196, 196],
        })
      ),
    z: new Decimal(-1),
    navigatable$: of(false),
  });

  createLandmark({
    name: 'stream',
    displayName: 'Stream',
    description: 'The Stream. Maybe you can catch something?',
    shape: new CompositeShape({
      tagRange: new Decimal(1.5),
    })
      .addShape(
        createLine(new Vector(-20, -1000), new Vector(-20, 10000), {
          thickness: new Decimal(10),
          fillColor: [0, 0, 255],
        })
      )
      .addShape(
        createLine(new Vector(-20, -1000), new Vector(1000, -1000), {
          thickness: new Decimal(10),
          fillColor: [0, 0, 255],
        })
      )
      .addShape(
        createLine(new Vector(1000, -1000), new Vector(1000, 1000), {
          thickness: new Decimal(10),
          fillColor: [0, 0, 255],
        })
      )
      .addShape(
        createLine(new Vector(-20, 1200), new Vector(500, 1200), {
          thickness: new Decimal(10),
          fillColor: [0, 0, 255],
        })
      ),
    z: new Decimal(-10),
    navigatable$: ri.landmarks.stream.visited$,
    tags: new Set(['fishing']),
    centralTags: new Set(['water']),
  });

  createLandmark({
    name: 'lake',
    displayName: 'Lake',
    description: 'The Lake. Fish and more! Some plants grow on the shores.',
    shape: new Region({
      name: 'Lake',
      bottomLeft: new Vector(500, 1000),
      width: new Decimal(1000),
      height: new Decimal(1000),
      fillColor: [0, 0, 255],
      tagRange: new Decimal(1),
    }),
    z: new Decimal(-10),
    navigatable$: ri.landmarks.lake.visited$,
    tags: new Set(['fishing', 'plants']),
    centralTags: new Set(['water']),
  });

  createLandmark({
    name: 'plains',
    displayName: 'Great Plains',
    description: 'The Plains! Pretty plain.',
    shape: new Region({
      name: 'Plains',
      topLeft: new Vector('-10e3', '10e3'),
      width: new Decimal(20e3),
      height: new Decimal(20e3),
      fillColor: [170, 170, 170],
    }),
    navigatable$: of(false),
    z: new Decimal(-100),
    tags: new Set([]),
  });

  createLandmark({
    name: 'forest',
    displayName: 'Midway Park',
    description: 'The Forest! Plants and animals abound.',
    shape: new Region({
      name: 'Midway Park',
      bottomLeft: new Vector('-2e3', '500'),
      width: new Decimal(4000),
      height: new Decimal(1000),
      fillColor: [34, 139, 34],
      // fillColor: [0, 255, 0],
    }),
    navigatable$: ri.landmarks.crossroads.visited$,
    z: new Decimal(-50),
    tags: new Set(['forest', 'plants', 'animals']),
  });

  createLandmark({
    name: 'darkForest',
    displayName: 'The Dark Forest',
    description: 'The Dark Forest! Beware the trees!',
    shape: new Region({
      name: 'The Dark Forest',
      topLeft: new Vector('-40000', '100000'),
      width: new Decimal(80000),
      height: new Decimal(90000),
      // fillColor: [34, 139, 34, 50],
      fillColor: [0, 255, 0],
    }),
    navigatable$: ri.landmarks.crossroads.visited$,
    z: new Decimal(-50),
    tags: new Set(['forest', 'plants', 'animals']),
  });

  createLandmark({
    name: 'library',
    displayName: 'The Grand Library',
    description: 'The Grand Library! A place of leisure and learning.',
    shape: new CompositeShape({}).addShape(
      new Region({
        name: 'Library',
        topLeft: new Vector(-1000 - 100, 1000 + 100),
        width: new Decimal(100),
        height: new Decimal(200),
        fillColor: [220, 220, 255],
        strokeColor: [45, 20, 5],
      })
    ),
    navigatable$: ri.landmarks.crossroads.visited$,
    tags: new Set(['books']),
  });

  createLandmark({
    name: 'monastery',
    displayName: 'Monastery',
    description: `The Monastery! A place of peace and tranquility. You feel at home here.`,
    shape: new Vector(-5000, 1000),
    navigatable$: ri.landmarks.library.visited$,
  });

  createLandmark({
    name: 'mountain',
    displayName: 'Mountain',
    description: 'The Mountain! You see a big sign: "UNDER CONSTRUCTION".',
    shape: new Vector(0, 5000),
    navigatable$: ri.landmarks.forest.visited$,
  });

  createLandmark({
    name: 'mine',
    displayName: 'Mine',
    description: 'The Mine! You see a big sign: "UNDER CONSTRUCTION".',
    shape: new Vector(0, 10000),
    navigatable$: ri.landmarks.mountain.visited$,
  });
}
